import {
  ComplianceFilingResponsibleEntity,
  ComplianceStatus,
  Country,
  ObligationRulesCategory,
  SalesTaxFilingStatusEnum,
} from '@prisma/client';
import { State } from 'country-state-city';
import {
  SyntheticComplianceStatus,
  TComplianceItem,
} from '@commenda/types/src/compliance-calendar/compliances/types';
import type { UnifiedFiling } from '@commenda/types/src/compliance-calendar/compliances/unified-filing';

export type { UnifiedFiling } from '@commenda/types/src/compliance-calendar/compliances/unified-filing';

interface SalesTaxFilingForMapping {
  id: string;
  filing_id: string;
  form_title: string;
  country: string;
  state: string;
  filing_due_date: string;
  filing_frequency: string;
  tax_period_start_date: string;
  tax_period_end_date: string;
  reporting_period_start_date: string;
  reporting_period_end_date: string;
  status: SalesTaxFilingStatusEnum;
  filed_at: string | null;
  Corporation?: { id?: number; companyLegalName?: string } | null;
  assignedUser?: {
    id: string;
    name?: string | null;
    email?: string | null;
  } | null;
  ServiceRequest?: { id?: string } | null;
}

function mapSalesTaxStatusToSynthetic(
  status: SalesTaxFilingStatusEnum
): SyntheticComplianceStatus {
  switch (status) {
    case SalesTaxFilingStatusEnum.FILED:
      return SyntheticComplianceStatus.FILED;
    case SalesTaxFilingStatusEnum.OVERDUE:
      return SyntheticComplianceStatus.OVERDUE;
    case SalesTaxFilingStatusEnum.SCHEDULED:
    case SalesTaxFilingStatusEnum.ACCRUING:
    case SalesTaxFilingStatusEnum.ACTIVE:
    default:
      return SyntheticComplianceStatus.UPCOMING;
  }
}

function mapSalesTaxStatusToComplianceStatus(
  status: SalesTaxFilingStatusEnum
): ComplianceStatus {
  switch (status) {
    case SalesTaxFilingStatusEnum.FILED:
      return ComplianceStatus.COMPLETED;
    case SalesTaxFilingStatusEnum.OVERDUE:
      return ComplianceStatus.NOT_FILED;
    default:
      return ComplianceStatus.NOT_FILED;
  }
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function deriveSalesTaxVersion(filing: SalesTaxFilingForMapping): string {
  if (!filing.reporting_period_end_date) return '';

  const date = new Date(filing.reporting_period_end_date + 'T00:00:00');
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const freq = filing.filing_frequency;

  if (freq === 'ANNUAL_CALENDAR_YEAR' || freq === 'FISCAL_YEAR') {
    return `FY ${year}`;
  }
  if (freq === 'QUARTERLY' || freq === 'QUARTERLY_PREPAY_MONTHLY') {
    const quarter = Math.ceil(month / 3);
    return `Q${quarter} ${year}`;
  }
  if (freq === 'SEMI_ANNUALLY') {
    const half = month <= 6 ? 1 : 2;
    return `H${half} ${year}`;
  }
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

function deriveSalesTaxEffectiveYear(
  filing: SalesTaxFilingForMapping
): number | undefined {
  if (!filing.tax_period_end_date) return undefined;
  const date = new Date(filing.tax_period_end_date + 'T00:00:00');
  return date.getFullYear();
}

export function mapComplianceItemToUnifiedFiling(
  item: TComplianceItem
): UnifiedFiling {
  return {
    id: item.id,
    source: 'COMPLIANCE_ITEM',

    title: item.title,
    dueDate: item.dueDate,
    category:
      item.category ?? ObligationRulesCategory.DIRECT_TAX,
    version: item.version,
    state: item.state,

    status: item.status,
    syntheticStatus: item.syntheticStatus,
    mode: item.mode ?? null,

    effectiveStartDate: item.effectiveStartDate,
    effectiveEndDate: item.effectiveEndDate,
    effectiveYear: item.effectiveYear,

    corporationId: item.corporationId,
    corporation: item.corporation,

    assignees: item.assignees ?? [],
    assignedAccountant: item.assignedAccountant,

    isCustom: item.isCustom,
    hasPaidForFiling: item.hasPaidForFiling,
    blockedOn: item.blockedOn,

    serviceRequestId: item.serviceRequestId,

    _complianceItem: item,
  };
}

export function mapSalesTaxFilingToUnifiedFiling(
  filing: SalesTaxFilingForMapping
): UnifiedFiling {
  const assignees: { id: string; name: string; email: string }[] = [];
  if (filing.assignedUser) {
    assignees.push({
      id: filing.assignedUser.id,
      name: filing.assignedUser.name ?? '',
      email: filing.assignedUser.email ?? '',
    });
  }

  const stateName = filing.state
    ? State.getStateByCodeAndCountry(
        filing.state,
        filing.country || 'US'
      )?.name || filing.state
    : undefined;

  const title = stateName
    ? `${filing.form_title} - ${stateName}`
    : filing.form_title;

  return {
    id: filing.id,
    source: 'SALES_TAX_FILING',

    title,
    dueDate: filing.filing_due_date,
    category: ObligationRulesCategory.INDIRECT_TAX,
    version: deriveSalesTaxVersion(filing),
    state: filing.state,

    status: mapSalesTaxStatusToComplianceStatus(filing.status),
    syntheticStatus: mapSalesTaxStatusToSynthetic(filing.status),

    effectiveStartDate: filing.tax_period_start_date,
    effectiveEndDate: filing.tax_period_end_date,
    effectiveYear: deriveSalesTaxEffectiveYear(filing),

    corporationId: filing.Corporation?.id ?? 0,
    corporation: {
      id: filing.Corporation?.id ?? 0,
      companyDisplayName: filing.Corporation?.companyLegalName ?? '',
      incorporationCountry: (filing.country as Country) ?? Country.US,
    },

    assignees,
    assignedAccountant: null,

    isCustom: false,
    blockedOn: ComplianceFilingResponsibleEntity.ACCOUNTANT,

    serviceRequestId: filing.ServiceRequest?.id,
    salesTaxFilingId: filing.filing_id,
  };
}
